/**
 * Maps the API's real shapes onto what the workflow screens show (ID26).
 *
 * Pure functions, unit-tested — the store does the fetching, these decide what
 * a response MEANS: which order on a shared trip, which warehouse's slot,
 * whether a pallet is holdable cargo or already shipped.
 */

import { orderIsFinished } from './orderStatus'

/**
 * The server answered, and its answer was a no: what it said is what the
 * driver is told. Never "check the connection" — the connection was fine.
 */
export class LookupAnswerError extends Error {}

export class LookupNotFoundError extends LookupAnswerError {}

/**
 * One of the pallets a bare identifier could mean (task 321, 2221): whose it
 * is, where it is now, and whether it is still on hand.
 */
export type PalletCandidate = {
  partnerId: number
  partnerName: string
  place: string | null
  onHand: boolean
}

/**
 * More than one partner holds a pallet with this identifier, and the server
 * will not guess (409 "Ambiguous Pallet"). Carries the server's words and the
 * pallets it could mean, so a screen can ask which.
 */
export class SharedPalletError extends LookupAnswerError {
  constructor(message: string, readonly palletId: string, readonly candidates: PalletCandidate[]) {
    super(message)
  }
}

/** The candidates a 409 "Ambiguous Pallet" problem carries; none when it carries none. */
export function readCandidates(body: unknown): PalletCandidate[] {
  const list = (body as { candidates?: unknown } | null)?.candidates
  if (!Array.isArray(list)) return []
  return list
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object' && typeof (c as { partnerId?: unknown }).partnerId === 'number')
    .map(c => ({
      partnerId: c.partnerId as number,
      partnerName: typeof c.partnerName === 'string' && c.partnerName.trim() ? c.partnerName : `Partner ${c.partnerId}`,
      place: typeof c.place === 'string' && c.place.trim() ? c.place : null,
      onHand: c.onHand !== false,
    }))
}

// ── Pallet: GET /api/Pallet/{palletIdentifier} → PalletResponseDto ──────────

export type PalletInfo = {
  palletId: string
  productNumber: string
  productDescription: string
  commodity: string
  caseQuantity: number
  weight: number
  /** Date part only — when it arrived, not a timestamp. */
  receivedDate: string
  /** Where the system thinks it is right now. */
  currentLocation: string
  onHold: boolean
  holdComments: string
  shipped: boolean
  /** Carry-over stock: older, and first out for a line that accepts it (decision 0016). */
  carryOver: boolean
  /** Whose pallet it is, when the server said; a move sends it so a shared identifier means this one. */
  partnerId?: number | null
  /** A pallet two partners have, going with the move for a clerk in BOB to say whose (task 2221). */
  toClerk?: boolean
  /**
   * Found in the open container while the data has it here instead -- another container, or a
   * floor location (Mark, 2026-10-15): it is moved anyway, from there, and the server records
   * Wrong Container (utils/containerWork.ts).
   */
  dataPlace?: string
}

type PalletDto = {
  palletIdentifier?: string | null
  productNumber?: string | null
  productDescription?: string | null
  commodity?: string | null
  caseQuantity?: number | null
  weight?: number | null
  receivedDate?: string | null
  onHold?: boolean | null
  holdComments?: string | null
  storageArea?: string | null
  storageName?: string | null
  shippedOnDate?: string | null
  carryOver?: boolean | null
  partnerId?: number | null
}

export function mapPallet(dto: PalletDto): PalletInfo {
  return {
    palletId: dto.palletIdentifier ?? '',
    productNumber: dto.productNumber ?? '',
    productDescription: dto.productDescription ?? '',
    commodity: dto.commodity ?? '',
    caseQuantity: dto.caseQuantity ?? 0,
    weight: dto.weight ?? 0,
    receivedDate: (dto.receivedDate ?? '').slice(0, 10),
    currentLocation: dto.storageName?.trim() || dto.storageArea?.trim() || '—',
    onHold: dto.onHold ?? false,
    holdComments: dto.holdComments ?? '',
    shipped: dto.shippedOnDate != null,
    carryOver: dto.carryOver ?? false,
    partnerId: typeof dto.partnerId === 'number' ? dto.partnerId : null,
  }
}

// ── Location: GET /api/InventoryLocation (all) → match the scanned name ─────

export type LocationInfo = {
  /** The NAME, as scanned and as shown — not the database id. */
  locationId: string
  /**
   * The floor location's real id. The move command addresses the floor by id
   * because a location name is only unique per warehouse; without this the
   * screen would have to resolve the name at flush time, which is exactly what
   * an offline queue cannot do.
   */
  inventoryLocationId: string | null
  warehouseName: string
  warehouseCode: string
}

type LocationDto = {
  id?: string | null
  name?: string | null
  warehouseId?: number | null
  warehouse?: { name?: string | null, code?: string | null } | null
}

/**
 * A location barcode is just the slot name, and slot names are only unique
 * WITHIN a warehouse — the same "A-07" can exist in two buildings. Guessing
 * would file the move against the wrong one, so an ambiguous name is refused
 * with the warehouses spelled out.
 */
export function resolveLocation(all: LocationDto[], scannedName: string, warehouseId: number | null = null): LocationInfo {
  const wanted = scannedName.trim().toUpperCase()
  const named = all.filter(l => (l.name ?? '').trim().toUpperCase() === wanted)
  // A tablet that knows its warehouse (utils/warehouses.ts) is not guessing:
  // of several locations with this name, the one in ITS warehouse is the one
  // the driver is standing at. Only when that does not settle it is the name
  // still refused as ambiguous.
  const here = warehouseId === null ? [] : named.filter(l => l.warehouseId === warehouseId)
  const matches = here.length === 1 ? here : named

  if (matches.length === 0) {
    throw new LookupNotFoundError(`Location ${scannedName} was not found`)
  }
  if (matches.length > 1) {
    const warehouses = matches
      .map(m => m.warehouse?.code || m.warehouse?.name || '?')
      .join(', ')
    throw new LookupNotFoundError(
      `Location ${scannedName} exists in more than one warehouse (${warehouses}) — it needs a warehouse-specific code`,
    )
  }

  const match = matches[0]!
  return {
    locationId: match.name ?? scannedName,
    inventoryLocationId: match.id ?? null,
    warehouseName: match.warehouse?.name ?? '—',
    warehouseCode: match.warehouse?.code ?? '',
  }
}

// ── Trip: GET /api/orders/search?q= → exact trip match ───────────────

export type TripInfo = {
  tripNumber: string
  orderNumber: string
  /** Needed to read the order's loaded pallets — that route is keyed by partner. */
  partnerId: number | null
  customerName: string
  destination: string
  orderStatus: string
  door: string
  palletsRequired: number
  palletsLoaded: number
  /** Product codes from the order's lines — what may legitimately be loaded. */
  productNumbers: string[]
  /**
   * Pallets wanted per PRODUCT. An order asking for three products is three
   * separate things to fetch, and a single total cannot tell a driver which
   * one is still short.
   */
  lines: OrderLineNeed[]
  /** A trip can carry several orders; the card shows the first and says so. */
  ordersOnTrip: number
  /** Where the chosen order sits in the loading sequence, 1-based. */
  loadPosition: number
  /** The order to move to once this one is finished, if the truck has one. */
  nextOrderNumber: string | null
  /**
   * Pallets the whole TRIP asks for, across every order on it.
   *
   * A trailer is one trailer: the diagram is sized by what the truck holds,
   * not by the order currently being worked.
   */
  tripPalletsRequired: number
  /**
   * Pallets ALREADY on the truck from orders earlier in the loading sequence.
   *
   * Their positions are taken before this order's first pallet, so this is the
   * offset the diagram starts filling at. Counted from what those orders
   * actually LOADED, not what they asked for — a short prior order leaves the
   * next pallet in the cell the freight physically occupies.
   */
  tripPalletsLoadedBefore: number
  /**
   * The order that has to go on this truck first, or '' when this one is next.
   *
   * Set only from orders the trip lookup actually returned, so it is a reason
   * to show, never a claim that the way is clear — the API refuses an
   * out-of-sequence ship regardless.
   */
  blockedBy: string
  /**
   * The order that has to come OFF this truck first, or '' when this one can.
   *
   * The reverse of blockedBy: pallets come off the door end first, so the
   * nearest LATER order that still has pallets aboard is in the way. Judged on
   * the loaded counts the search returned; the API refuses an out-of-sequence
   * unship regardless.
   */
  unloadBlockedBy: string
}

type OrderLineDto = {
  productNumber?: string | null
  productDescription?: string | null
  palletQuantity?: number | null
    carryOverAcceptable?: boolean | null
}

/** What one product on an order still needs, for the per-product bars. */
export type OrderLineNeed = {
  productNumber: string
  productDescription: string
  palletsRequired: number
  /**
   * The line takes carry-over stock — and therefore takes it FIRST: while any
   * is on the floor, a newer pallet of the product is refused (decision 0016).
   * True by default in the data model, so most lines want it.
   */
  carryOverAcceptable: boolean
}

type OrderDto = {
  orderNumber?: string | null
  partnerId?: number | null
  /**
   * Where this order sits in the truck's loading sequence
   * (orders.load_order, default 1). On the response DTO but not the
   * update DTO — it is set upstream, never from here. Still optional: the
   * paged list is a different DTO, and an order number tie-break covers it.
   */
  loadOrder?: number | null
  gang?: string | null
  warehouseId?: number | null
  orderLines?: OrderLineDto[] | null
  customerName?: string | null
  tripNumber?: string | null
  distributionCenter?: string | null
  orderStatus?: string | null
  /** The door the truck is at, by its name as painted over it (BOB sends its id too; Mark, 2026-10-20). */
  doorName?: string | null
  totalPalletsRequired?: number | null
  totalPalletsLoaded?: number | null
}

/**
 * The search endpoint matches CONTAINS across several fields, so a query for
 * trip "T-45" also returns "T-451" — only exact trip matches count here.
 */
/** One order on a truck, as the list of trucks to load (or unload) holds it. */
export type OrderRowInfo = {
  tripNumber: string
  orderNumber: string
  partnerId: number | null
  /** The partner's name, for the screens that name whose truck it is. */
  partnerName?: string
  /** What the order asks for and what is aboard; null only if the server did not say. */
  palletsRequired: number | null
  palletsLoaded: number | null
  /**
   * Pallets on the floor ready to go on this order now (task 2216): the server's count, never
   * more than the order still wants, each pallet counted once across the trucks at the doors.
   * 0 when the server did not say.
   */
  palletsReady?: number
  customerName: string
  door: string
  /** The server's status name, shown verbatim. */
  status: string
  /** Which gang the order is assigned to; '' when none. */
  gang: string
  /** The warehouse of that gang, set with it when the truck docked; null when it has none. */
  warehouseId: number | null
  /** Only a truck at a door, with a trip to load against, can be worked. */
  loadable: boolean
  /** Where this order sits in its trip's loading sequence, 1-based. */
  loadPosition: number
  /** How many orders share this trip — 1 for the ~97% that ride alone. */
  ordersOnTrip: number
  /**
   * The order that has to be loaded first, or '' when this one is next.
   *
   * Judged only against trip-mates ON THIS LIST. A sibling the day's query did
   * not return cannot be judged, and guessing would either strand the truck or
   * wave through a load the server will refuse anyway.
   */
  blockedBy: string
  /** When the truck was put at its door (or, failing that, checked in): what its wait is counted from. */
  waitingSince?: string | null
  /** The order's type in BOB (task 2502): Direct Delivery, Commercial Cross Dock, Preload; '' for a Standard order. */
  orderType?: string
}

/** The statuses a forklift can put pallets on, and the one it takes them back off. */
// Unload: an order sent back to be reloaded, or one whose driver was turned away (task 3483, Mark
// 2026-10-26): its pallets come off before the order waits for its truck again.
const WORKABLE_STATUSES = { load: ['docked', 'loading'], unload: ['reloading', 'turningaway'] }

type TruckLoadDto = {
  partnerId?: number | null
  partnerName?: string | null
  tripNumber?: string | null
  /** The door the truck is at, by its name: what a door scan is checked against. */
  doorName?: string | null
  gang?: string | null
  warehouseId?: number | null
  warehouseCheckedInDate?: string | null
  dockedDate?: string | null
  /** The truck's inspection (task 2215): NotAsked, NotInspected, Passed, Failed (a failed answer still to correct) or Corrected. */
  inspectionState?: string | null
  /** Why the server will not load it because of its inspection; null when it will. */
  loadingBlockedReason?: string | null
  orders?: Array<{
    orderNumber?: string | null
    /** The order's type (task 2502): 1 is Standard. */
    subtypeId?: number | null
    subtypeName?: string | null
    customerName?: string | null
    orderStatus?: string | null
    loadPosition?: number | null
    palletsRequired?: number | null
    palletsLoaded?: number | null
    /** On the floor ready to go on it now (task 2216). */
    palletsReady?: number | null
    blockedBy?: string | null
    lines?: Array<{
      productNumber?: string | null
      productDescription?: string | null
      palletsRequired?: number | null
      carryOverAcceptable?: boolean | null
    }> | null
    pallets?: ShippedPalletDto[] | null
  }> | null
}

/** One order on the truck a forklift is working. */
export type TruckOrderInfo = {
  orderNumber: string
  customerName: string
  /** The server's status name, shown verbatim. */
  status: string
  /** Its place on the truck, 1-based: 1 goes on first. */
  loadPosition: number
  palletsRequired: number
  /** What it asks for, one row per product. */
  lines: OrderLineNeed[]
  /** The pallets the server has aboard for it. */
  pallets: LoadedPalletInfo[]
}

/** One truck in full, as the Load Truck screen works it. */
export type TruckLoadInfo = {
  partnerId: number
  partnerName: string
  tripNumber: string
  door: string
  gang: string
  /** The truck's inspection (task 2215): NotAsked, NotInspected, Passed, Failed (a failed answer still to correct) or Corrected. */
  inspectionState: string
  /** Why the server will not load it because of its inspection; null when it will. */
  loadingBlockedReason: string | null
  /** In loading sequence. */
  orders: TruckOrderInfo[]
}

/**
 * One truck, read from `GET /api/truck-loads/{partner}/{trip}`: its orders in the sequence
 * they go on, what each asks for per product, and the pallets already aboard each. Which
 * order is being loaded is not read from here; the screen works it out from this and from its
 * own unsent work (utils/truckLoad.ts).
 */
export function mapTruckLoad(truck: TruckLoadDto): TruckLoadInfo {
  return {
    partnerId: truck.partnerId ?? 0,
    partnerName: (truck.partnerName ?? '').trim(),
    tripNumber: (truck.tripNumber ?? '').trim(),
    door: (truck.doorName ?? '').trim(),
    gang: (truck.gang ?? '').trim(),
    inspectionState: (truck.inspectionState ?? 'NotAsked').trim(),
    loadingBlockedReason: truck.loadingBlockedReason?.trim() || null,
    orders: (truck.orders ?? []).map((order, index) => ({
      orderNumber: order.orderNumber ?? '—',
      customerName: order.customerName ?? '—',
      status: String(order.orderStatus ?? '').trim(),
      loadPosition: order.loadPosition ?? index + 1,
      palletsRequired: order.palletsRequired ?? 0,
      lines: (order.lines ?? [])
        .filter(line => (line.productNumber ?? '').trim() !== '')
        .map(line => ({
          productNumber: (line.productNumber ?? '').trim(),
          productDescription: (line.productDescription ?? '').trim(),
          palletsRequired: line.palletsRequired ?? 0,
          carryOverAcceptable: line.carryOverAcceptable ?? true,
        })),
      pallets: mapShippedPallets(order.pallets ?? []),
    })),
  }
}

/** The partners whose trucks on the list carry this trip number: how a trip scanned bare finds whose it is. */
export function partnersWithTrip(body: { trucks?: TruckLoadDto[] | null } | null | undefined, tripNumber: string): number[] {
  const wanted = tripNumber.trim().toUpperCase()
  const partners = (body?.trucks ?? [])
    .filter(truck => (truck.tripNumber ?? '').trim().toUpperCase() === wanted && truck.partnerId != null)
    .map(truck => truck.partnerId!)
  return [...new Set(partners)]
}

/**
 * The trucks to load or unload, as rows the list screen holds: one per order, each knowing
 * its truck, its place on it, its counts and what is in its way.
 *
 * Read from `GET /api/truck-loads`, the tablet's own read (owner, 2026-10-12). The server
 * puts the trucks together - which orders ride together, in what sequence, how many pallets
 * each has aboard, and whose turn it is - so nothing here works any of that out. It used to:
 * three reads of the general order list, one more per order for its count, and the sequence
 * rebuilt on the device, which could disagree with the server that then refused the load.
 *
 * Trucks arrive in door order and their orders in loading sequence, and the rows keep that.
 * A row is `loadable` when its status allows the work AND it has a trip to work against;
 * what is ahead of it on the truck is `blockedBy`, said by the server.
 */
export function mapTruckLoads(body: { trucks?: TruckLoadDto[] | null } | null | undefined, direction: 'load' | 'unload'): OrderRowInfo[] {
  const workable = WORKABLE_STATUSES[direction]
  return (body?.trucks ?? []).flatMap((truck) => {
    const tripNumber = (truck.tripNumber ?? '').trim()
    const orders = truck.orders ?? []
    return orders.map((order, index) => {
      const status = String(order.orderStatus ?? '').trim()
      return {
        tripNumber,
        orderNumber: order.orderNumber ?? '—',
        partnerId: truck.partnerId ?? null,
        partnerName: (truck.partnerName ?? '').trim(),
        palletsRequired: order.palletsRequired ?? null,
        palletsLoaded: order.palletsLoaded ?? null,
        palletsReady: order.palletsReady ?? 0,
        customerName: order.customerName ?? '—',
        door: (truck.doorName ?? '').trim(),
        status: status === '' ? '—' : status,
        gang: (truck.gang ?? '').trim(),
        warehouseId: truck.warehouseId ?? null,
        waitingSince: truck.dockedDate ?? truck.warehouseCheckedInDate ?? null,
        loadable: tripNumber !== '' && workable.includes(status.toLowerCase()),
        loadPosition: order.loadPosition ?? index + 1,
        ordersOnTrip: orders.length,
        blockedBy: (order.blockedBy ?? '').trim(),
        orderType: order.subtypeId && order.subtypeId !== 1 ? (order.subtypeName ?? '').trim() : '',
      }
    })
  })
}

/**
 * Fold a freshly fetched list into the rows already on screen.
 *
 * The Orders screen refreshes itself every few seconds, and a background
 * refresh has a different job from the one the operator asks for by pressing
 * Refresh: it must bring the numbers up to date WITHOUT rearranging the screen
 * under a driver's thumb. On a forklift a row that moves between the eye and
 * the finger is a mis-tap, and a mis-tap starts the wrong truck.
 *
 * So surviving rows keep their position and their identity — the same object is
 * updated in place, which also means a per-row pallet-count fetch still in
 * flight lands on a row that is still on screen instead of an orphan. Orders
 * that appeared since the last read are appended; orders that dropped out of
 * the day's work are removed. Only an operator-initiated load re-sorts, which
 * is what the Refresh button is for.
 *
 * Pallet counts are carried forward when a read does not say them, so a number the
 * operator is reading is never blanked by a refresh.
 */
export function mergeOrderRows(current: OrderRowInfo[], incoming: OrderRowInfo[]): OrderRowInfo[] {
  const fresh = new Map(incoming.map(row => [rowKey(row), row]))
  const kept = new Set<string>()
  const merged: OrderRowInfo[] = []

  for (const row of current) {
    const key = rowKey(row)
    const update = fresh.get(key)
    if (!update) continue      // no longer part of today's work

    kept.add(key)
    Object.assign(row, update, {
      palletsRequired: update.palletsRequired ?? row.palletsRequired,
      palletsLoaded: update.palletsLoaded ?? row.palletsLoaded,
    })
    merged.push(row)
  }

  for (const row of incoming) {
    if (!kept.has(rowKey(row))) merged.push(row)
  }

  return merged
}

/** Partner + order is the identity; a trip number can be edited, and is not. */
function rowKey(row: OrderRowInfo): string {
  return `${row.partnerId ?? ''}|${row.orderNumber.trim().toUpperCase()}`
}

/** One pallet the server says is loaded on an order. */
export type LoadedPalletInfo = {
  palletIdentifier: string
  productNumber: string
  productDescription: string
  caseQuantity: number
}

type ShippedPalletDto = {
  palletIdentifier?: string | null
  productNumber?: string | null
  productDescription?: string | null
  caseQuantity?: number | null
}

/**
 * The order's loaded pallets, as the barcode sheet lists them.
 *
 * A row without an identifier cannot be turned into a barcode, so it is
 * dropped rather than rendered as an empty label. Identifiers are deduped:
 * the same pallet can arrive both from the server and from work this device
 * queued but has not yet synced.
 */
export function mapShippedPallets(rows: ShippedPalletDto[]): LoadedPalletInfo[] {
  const seen = new Set<string>()
  const mapped: LoadedPalletInfo[] = []
  for (const row of rows) {
    const palletIdentifier = (row.palletIdentifier ?? '').trim()
    if (palletIdentifier === '' || seen.has(palletIdentifier)) continue
    seen.add(palletIdentifier)
    mapped.push({
      palletIdentifier,
      productNumber: row.productNumber ?? '',
      productDescription: row.productDescription ?? '',
      caseQuantity: row.caseQuantity ?? 0,
    })
  }
  return mapped
}

/**
 * One row per PRODUCT, quantities summed.
 *
 * An order can list the same product on several lines; the driver cares how
 * many of that product to fetch, not how the paperwork split them up.
 */
function needsByProduct(order: OrderDto): OrderLineNeed[] {
  const byProduct = new Map<string, OrderLineNeed>()
  for (const line of order.orderLines ?? []) {
    const productNumber = (line.productNumber ?? '').trim()
    if (productNumber === '') continue
    const existing = byProduct.get(productNumber)
    if (existing) {
      existing.palletsRequired += line.palletQuantity ?? 0
      // Any line of the product that accepts carry-over makes the product want it first.
      existing.carryOverAcceptable = existing.carryOverAcceptable || (line.carryOverAcceptable ?? true)
      continue
    }
    byProduct.set(productNumber, {
      productNumber,
      productDescription: (line.productDescription ?? '').trim(),
      palletsRequired: line.palletQuantity ?? 0,
      carryOverAcceptable: line.carryOverAcceptable ?? true,
    })
  }
  return [...byProduct.values()]
}

export function resolveTrip(
  orders: OrderDto[],
  scannedTrip: string,
  /**
   * The order the operator actually picked. A trip can carry several orders —
   * 920 of them do in live data, up to seven on one truck — and they are
   * loaded ONE AT A TIME, each finished before the next starts. Without this
   * the screen took whichever match the search returned first, so tapping the
   * third order could file every pallet against the first.
   */
  wantedOrderNumber?: string,
): TripInfo {
  const wanted = scannedTrip.trim().toUpperCase()
  const matches = orders.filter(o => (o.tripNumber ?? '').trim().toUpperCase() === wanted)

  if (matches.length === 0) {
    throw new LookupNotFoundError(`No order found for trip ${scannedTrip}`)
  }

  // The loading sequence. load_order when the API sends it; order number is
  // the tie-break, and the whole sort while the field is still unexposed.
  matches.sort((a, b) =>
    (a.loadOrder ?? 1) - (b.loadOrder ?? 1)
    || (a.orderNumber ?? '').localeCompare(b.orderNumber ?? ''))

  const asked = (wantedOrderNumber ?? '').trim().toUpperCase()
  const chosen = asked === ''
    ? undefined
    : matches.find(o => (o.orderNumber ?? '').trim().toUpperCase() === asked)

  if (asked !== '' && !chosen) {
    // Naming an order that is not on this trip is a mix-up worth stopping on,
    // not something to paper over by loading a different customer's freight.
    throw new LookupNotFoundError(`Order ${wantedOrderNumber} is not on trip ${scannedTrip}`)
  }

  // No order named (a bare trip scan) falls back to the first match, which is
  // right for the ~97% of trips carrying exactly one.
  const first = chosen ?? matches[0]!

  // Everything ahead of it on the truck that is not done with yet. The screen
  // shows the first one as the reason; the API refuses the ship either way.
  const blockedBy = matches
    .filter(o => (o.loadOrder ?? 1) < (first.loadOrder ?? 1)
      && !orderIsFinished(String(o.orderStatus ?? '')))
    .map(o => o.orderNumber ?? '—')[0] ?? ''

  // And the other way round, for unloading: the nearest LATER order that still
  // has pallets aboard is between the door and this one's freight.
  const unloadBlockedBy = matches
    .filter(o => (o.loadOrder ?? 1) > (first.loadOrder ?? 1) && (o.totalPalletsLoaded ?? 0) > 0)
    .map(o => o.orderNumber ?? '—')
    .pop() ?? ''

  return {
    tripNumber: first.tripNumber ?? scannedTrip,
    orderNumber: first.orderNumber ?? '—',
    partnerId: first.partnerId ?? null,
    customerName: first.customerName ?? '—',
    destination: first.distributionCenter ?? '—',
    orderStatus: String(first.orderStatus ?? '—'),
    door: first.doorName ?? '',
    palletsRequired: first.totalPalletsRequired ?? 0,
    palletsLoaded: first.totalPalletsLoaded ?? 0,
    productNumbers: needsByProduct(first).map(line => line.productNumber),
    lines: needsByProduct(first),
    ordersOnTrip: matches.length,
    loadPosition: matches.indexOf(first) + 1,
    // Null on the last order, which is what tells the screen to stop advancing
    // and send the operator back to the list.
    nextOrderNumber: matches[matches.indexOf(first) + 1]?.orderNumber ?? null,
    blockedBy,
    unloadBlockedBy,
    tripPalletsRequired: matches.reduce((sum, o) => sum + (o.totalPalletsRequired ?? 0), 0),
    tripPalletsLoadedBefore: matches
      .slice(0, matches.indexOf(first))
      .reduce((sum, o) => sum + (o.totalPalletsLoaded ?? 0), 0),
  }
}
